import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, handleApiError } from '../lib/api';
import { DollarSign, Clock, TrendingUp, CreditCard, Wallet as WalletIcon, Download, Loader2 } from 'lucide-react';
import { ApiResponse, PaginatedResponse, Transaction, WalletStats, PaymentMethod } from '@ouagaliv/shared';

const txTypeLabels: Record<string, string> = {
  credit: 'Gain',
  debit: 'Débit',
  commission: 'Commission',
  refund: 'Remboursement',
  withdrawal: 'Retrait',
};

export default function Earnings() {
  const { user } = useAuth();
  const agentUser = user as any;
  const [stats, setStats] = useState<WalletStats | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [s, t] = await Promise.all([
        api.get<ApiResponse<WalletStats>>('/wallet/stats'),
        api.get<ApiResponse<PaginatedResponse<Transaction>>>('/wallet/transactions'),
      ]);
      if (s.data.success && s.data.data) setStats(s.data.data);
      if (t.data.success && t.data.data) setTransactions(t.data.data.items);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setWithdrawing(true);
    try {
      const res = await api.post<ApiResponse<Transaction>>('/wallet/withdraw', {
        amountFcfa: Number(withdrawAmount),
        paymentMethod: PaymentMethod.MOBILE_MONEY,
      });
      if (!res.data.success) throw new Error(res.data.error?.message || 'Retrait impossible');
      setWithdrawAmount('');
      setSuccess(`Retrait de ${Number(withdrawAmount).toLocaleString()} FCFA demandé`);
      fetchAll();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setWithdrawing(false);
    }
  };

  const formatAmount = (amount: number) => {
    const prefix = amount >= 0 ? '+' : '';
    return `${prefix}${amount.toLocaleString()} FCFA`;
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 size={48} className="text-primary animate-spin" />
      </div>
    );
  }

  const balance = stats?.currentBalance ?? 0;
  const earningsCards = [
    { label: 'Solde disponible', value: balance, icon: WalletIcon, color: 'text-primary' },
    { label: 'Total rechargé', value: stats?.totalRecharges ?? 0, icon: TrendingUp, color: 'text-green-600' },
    { label: 'Total retiré', value: stats?.totalWithdrawals ?? 0, icon: DollarSign, color: 'text-amber-600' },
    { label: 'En attente', value: stats?.pendingBalance ?? 0, icon: Clock, color: 'text-secondary' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-24">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-text">Mes gains</h1>
          <span className="badge badge-success">{agentUser?.balanceFcfa?.toLocaleString() || balance.toLocaleString()} FCFA</span>
        </div>

        {error && (
          <div className="card mb-4" style={{ borderColor: 'var(--color-error)' }}>
            <span style={{ color: 'var(--color-error)', fontSize: 14 }}>{error}</span>
          </div>
        )}
        {success && (
          <div className="card mb-4" style={{ borderColor: '#059669' }}>
            <span style={{ color: '#059669', fontSize: 14 }}>{success}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-6">
          {earningsCards.map((card, i) => (
            <div key={i} className="card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <card.icon size={24} className={card.color} />
                </div>
                <div>
                  <p className="text-xs text-text-secondary">{card.label}</p>
                  <p className="font-bold text-text">{card.value.toLocaleString()} FCFA</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-4 border-b border-border">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 py-2 text-center font-medium ${activeTab === 'overview' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary'}`}
          >
            Vue d'ensemble
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-2 text-center font-medium ${activeTab === 'history' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary'}`}
          >
            Historique
          </button>
        </div>

        {activeTab === 'overview' && (
          <div className="card space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                  <WalletIcon size={24} />
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Solde disponible</p>
                  <p className="text-xl font-bold text-text">{balance.toLocaleString()} FCFA</p>
                </div>
              </div>
            </div>

            <Link to="/recharge" className="flex items-center justify-between p-4 bg-green-50 rounded-lg" style={{ textDecoration: 'none' }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                  <DollarSign size={24} />
                </div>
                <div>
                  <p className="font-medium text-text">Recharger mon crédit</p>
                  <p className="text-xs text-text-secondary">Choisir un montant et voir la commission</p>
                </div>
              </div>
              <span className="btn btn-primary btn-sm">Recharger</span>
            </Link>

            <form onSubmit={handleWithdraw} className="p-4 bg-amber-50 rounded-lg space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                  <Clock size={24} />
                </div>
                <div>
                  <p className="font-medium text-text">Retirer vers Mobile Money</p>
                  <p className="text-xs text-text-secondary">Minimum 1 000 FCFA</p>
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  className="input-field"
                  value={withdrawAmount}
                  onChange={e => setWithdrawAmount(e.target.value)}
                  placeholder="Montant en FCFA"
                  min="1000"
                  max="1000000"
                  required
                />
                <button type="submit" className="btn btn-secondary" disabled={withdrawing}>
                  {withdrawing ? 'Retrait...' : 'Retirer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-text">Historique des transactions</h3>
              <div className="flex items-center gap-2">
                <Download size={18} className="text-text-secondary" />
                <span className="text-sm text-text-secondary">Exporter</span>
              </div>
            </div>
            {!transactions.length ? (
              <p className="text-sm text-text-secondary text-center py-6">Aucune transaction pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {transactions.map(tx => {
                  const amount = (tx.type === 'credit' || tx.type === 'refund' ? 1 : -1) * tx.amountFcfa;
                  return (
                    <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${amount >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                          {tx.type === 'withdrawal' ? <Download size={18} /> : <CreditCard size={18} />}
                        </div>
                        <div>
                          <p className="font-medium text-text capitalize">{txTypeLabels[tx.type] ?? tx.type}</p>
                          <p className="text-xs text-text-secondary">{formatDate(tx.createdAt)} {tx.rideId ? `• course` : ''}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatAmount(amount)}</p>
                        <p className="text-xs text-text-secondary">Solde: {tx.balanceAfterFcfa.toLocaleString()} FCFA</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
